import React, { useState, Suspense, useRef, useEffect } from 'react';

import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import './ParentRegistrationForm.css';
import { Input, Button, DropdownFilter } from 'geniebook-ui/dist';
import { option } from 'geniebook-ui/dist/components/DropdownFilter/selection';
import mockExamService from '@services/MockExamServices.service';
import { ErrorObject } from '@models/interfaces/global.interface';
import { MockExamParent } from '@models/interfaces/mock_exam.interface';
import { PAGE_LOGIN_PARENT } from '@utils/constants/mock-exam.constants';
import mockExamSplitName from '@utils/helpers/mockExamSplitName';

const defaultCountryCode = '65';
const EC_INVALID_EMAIL = '102-04';
const EC_MOBILE_NUM_INVALID = '102-10';

interface IParentReg {
  fullName: string;
  email: string;
  countryCode: string;
  mobileNumber: string;
}

const registerInitialValues: IParentReg = {
  fullName: '',
  email: '',
  countryCode: defaultCountryCode,
  mobileNumber: '',
};

const inputError: IParentReg = {
  fullName: '',
  email: '',
  countryCode: '',
  mobileNumber: '',
};

// validation rule using yup
const schema = yup
  .object({
    fullName: yup.string().trim().required('Full Name is required'),
    email: yup
      .string()
      .trim()
      .email('Email is not valid')
      .required('Email is required'),
    mobileNumber: yup
      .string()
      .trim()
      .matches(/^[0-9]+$/, 'Mobile number must be numbers')
      .required('Mobile Number is required'),
  })
  .required();

// options for country code dropdown, hardcode for now
const countryOptions: option<string | number>[] = [
  { label: 'Singapore +65', value: '65' },
  { label: 'Vietnam +84', value: '84' },
  { label: 'Malaysia +60', value: '60' },
  { label: 'United States +1', value: '1' },
  { label: 'China +86', value: '86' },
  { label: 'India +91', value: '91' },
  { label: 'Hong Kong +852', value: '852' },
  { label: 'Thailand +66', value: '66' },
  { label: 'More countries will be added soon. Stay tuned!', value: '' },
];

const ParentRegistrationForm = ({
  regParentData,
  setRegParentData,
  setError,
  setStep,
  redirectToStudentList,
}: {
  regParentData: MockExamParent;
  setRegParentData: React.Dispatch<React.SetStateAction<MockExamParent>>;
  setError: React.Dispatch<React.SetStateAction<ErrorObject>>;
  setStep: React.Dispatch<React.SetStateAction<string>>;
  redirectToStudentList: () => void;
}) => {
  const [mobilePopupOpen, setmobilePopupOpen] = useState<boolean>(false);
  const mobilePopupRef = useRef<HTMLDivElement>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IParentReg>({
    defaultValues: registerInitialValues,
    resolver: yupResolver(schema),
  });

  const [countryCode, setCountryCode] = useState<string | number>(
    defaultCountryCode,
  );
  const handleSetCountryCode = (value: string | number) => {
    setCountryCode(value);
    setmobilePopupOpen(false);
  };

  const registerMutation = useMutation(
    (registrationData: MockExamParent) =>
      mockExamService.registerParent(registrationData),
    {
      onSuccess(data) {
        if (data.response.data.error == true) {
          switch (data.response.data.error_code) {
            case EC_INVALID_EMAIL:
              inputError.email = data.response.data.error_msg;
              break;
            case EC_MOBILE_NUM_INVALID:
              inputError.mobileNumber = data.response.data.error_msg;
              break;
            default:
              setError({
                error: true,
                error_msg: data.response.data.error_msg,
                error_code: '',
              });
              break;
          }
        } else {
          setRegParentData(data.registrationData);
          redirectToStudentList();
        }
      },
      onError(error: Error) {
        setError({
          error: true,
          error_msg: error.message,
          error_code: '',
        });
      },
    },
  );

  const onSubmit = async (formData: {
    countryCode: string | number;
    fullName: string;
    email: string;
    mobileNumber: string;
  }) => {
    // reset error msg from API
    inputError.fullName = '';
    inputError.email = '';
    inputError.countryCode = '';
    inputError.mobileNumber = '';

    formData.countryCode = countryCode;

    const arrName = mockExamSplitName(formData.fullName);
    const requestData: MockExamParent = {
      first_name: arrName.first_name,
      last_name: arrName.last_name,
      email: formData.email,
      country_code: countryCode + '', // force to string
      mobile: formData.mobileNumber,
    };

    await registerMutation.mutateAsync(requestData);
  };

  useEffect(() => {
    setValue(
      'fullName',
      (regParentData['first_name'] + ' ' + regParentData['last_name']).trim(),
    );
    setValue('email', regParentData['email']);
    setCountryCode(
      regParentData['country_code'] == ''
        ? countryOptions[0].value
        : regParentData['country_code'],
    );
    setValue('mobileNumber', regParentData['mobile']);
  }, []);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleViewWidthChange);
    handleViewWidthChange();
    return () => {
      window.removeEventListener('resize', () => handleViewWidthChange);
    };
  }, []);

  useEffect(() => {
    if (mobilePopupOpen) handleViewWidthChange();
  }, [mobilePopupOpen]);

  useEffect(() => {
    if (mobilePopupRef.current !== null) {
      const dropdownLabel = mobilePopupRef.current.querySelectorAll(
        '.mock-exam-parent-reg-mobile-label span:last-child',
      );
      if (dropdownLabel.length > 0) {
        const selectedCountry = countryOptions.filter(
          el => el.value === countryCode,
        );
        if (selectedCountry.length > 0) {
          const splitCountry = selectedCountry[0].label.split(' ');
          dropdownLabel[0].innerHTML = splitCountry[splitCountry.length - 1];
        }
      }
    }
  }, [countryCode]);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      mobilePopupRef.current &&
      !mobilePopupRef.current.contains(event.target as Node)
    ) {
      setmobilePopupOpen(false);
    }
  };

  const handleViewWidthChange = () => {
    if (mobilePopupRef.current !== null) {
      const dropdownPopup = mobilePopupRef.current.querySelectorAll(
        '.mock-exam-parent-reg-mobile',
      );
      if (dropdownPopup.length > 0) {
        const parentWidth = mobilePopupRef.current.parentElement?.clientWidth;
        if (parentWidth !== undefined)
          dropdownPopup[0].setAttribute(
            'style',
            'width: ' + parentWidth + 'px',
          );
      }
    }
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-6 mb-0"
      >
        <p className="text-neutral-pencil mb-4">
          Already have an account?{' '}
          <span
            className="text-primary-apricot font-bold cursor-pointer"
            onClick={() => {
              setStep(PAGE_LOGIN_PARENT);
            }}
          >
            Log in
          </span>
        </p>
        <div className="flex -mt-6 max-sm:flex-col max-sm:gap-6">
          <Controller
            name="fullName"
            control={control}
            render={({ field }) => (
              <Input
                mode={'light'}
                variant={'outside'}
                {...field}
                placeholder="Enter your full name"
                className="w-full register-input"
                label="Full Name"
                error={
                  errors.fullName
                    ? errors.fullName.message
                    : inputError.fullName
                }
                disabled={registerMutation.isLoading}
                onChange={e => {
                  const value = e.target.value;
                  setValue('fullName', value.substring(0, 60));
                }}
              />
            )}
          />
        </div>
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <Input
              mode={'light'}
              variant={'outside'}
              {...field}
              placeholder="Enter your email"
              className="w-full register-input"
              label="Email"
              error={errors.email ? errors.email.message : inputError.email}
              disabled={registerMutation.isLoading}
            />
          )}
        />
        <div className="focus-within:text-primary-apricot text-primary-navyblue">
          <div
            className={
              'font-medium leading-4 text-base whitespace-nowrap' +
              (mobilePopupOpen ? ' text-primary-apricot' : '')
            }
          >
            Mobile Number
          </div>
          <div className="flex">
            <Controller
              name="countryCode"
              control={control}
              render={() => (
                <div
                  onClick={() => {
                    if (!registerMutation.isLoading)
                      setmobilePopupOpen(!mobilePopupOpen);
                  }}
                  ref={mobilePopupRef}
                  className="w-[30%]"
                >
                  <DropdownFilter
                    classNameBase={
                      '!mt-2 h-12 !text-neutral-pencil' +
                      (mobilePopupOpen
                        ? ' ring-2 ring-opacity-50 ring-primary-apricot border-primary-apricot'
                        : ' !border-neutral-notebook') +
                      (registerMutation.isLoading
                        ? ' !bg-neutral-notebook'
                        : ' !bg-white')
                    }
                    classNameOption="!text-neutral-pencil !bg-white z-10 !top-[3.1rem] shadow-md mock-exam-parent-reg-mobile"
                    classNameLabel="mock-exam-parent-reg-mobile-label"
                    setSelected={handleSetCountryCode}
                    options={countryOptions}
                    label={''}
                    selected={countryCode}
                  />
                </div>
              )}
            />
            <Controller
              name="mobileNumber"
              control={control}
              render={({ field }) => (
                <Input
                  mode={'light'}
                  variant={'outside'}
                  {...field}
                  placeholder="Mobile Number"
                  className="w-[70%] pl-2 register-input"
                  label={''}
                  error={
                    errors.mobileNumber
                      ? errors.mobileNumber.message
                      : inputError.mobileNumber
                  }
                  disabled={registerMutation.isLoading}
                  ref={mobileInputRef}
                />
              )}
            />
          </div>
        </div>
        <div>
          <Suspense>
            <button
              className="w-full"
              disabled={
                !(
                  watch('fullName') &&
                  watch('email') &&
                  watch('mobileNumber')
                ) || registerMutation.isLoading
              }
            >
              <Button
                className="!max-w-none w-full"
                variant="primary"
                disabled={
                  !(
                    watch('fullName') &&
                    watch('email') &&
                    watch('mobileNumber')
                  ) || registerMutation.isLoading
                }
              >
                {registerMutation.isLoading ? 'Processing...' : 'Register'}
              </Button>
            </button>
          </Suspense>
        </div>
        <div className="text-neutral-pencil text-xs">
          By submitting your phone number, we have your permission to contact
          you for our complimentary strengths analysis. See our{' '}
          <a
            href="https://geniebook.com/policies/privacy"
            className="text-primary-apricot text-xs cursor-pointer underline"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>
        </div>
      </form>
    </>
  );
};

export default ParentRegistrationForm;
